import React from 'react';
import { useTranslation } from 'react-i18next';
import { PayButton } from '../../../app/PayButton';
import { TrendingUpIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { CashbackBadge } from './CashbackBadge';

export interface CashbackBannerProps {
  /** The type of promotion to display */
  type: 'early_pay' | 'advance_deposit';
  /** Cashback percentage as decimal (e.g. 0.02 for 2%, 0.05 for 5%) */
  percentage: number;
  /** The formatted savings amount (e.g. "50 SAR") — only for early_pay */
  savingsAmount?: string;
  /** Days until the discount expires — only for early_pay */
  daysLeft?: number;
  /** CTA click handler */
  onActionClick: () => void;
  /** Optional className */
  className?: string;
}

/**
 * CashbackBanner — Promotional banner to incentivize early payments or advance deposits.
 *
 * Placed between the BalanceCard and the Invoices tab on the Wallet page.
 *
 * Two modes:
 * - `early_pay`       — "Pay invoice early and save 2%" with urgency countdown
 * - `advance_deposit` — "Deposit funds and get 5% cashback"
 *
 * @example
 * ```tsx
 * <CashbackBanner
 *   type="early_pay"
 *   percentage={0.02}
 *   savingsAmount="50 SAR"
 *   daysLeft={3}
 *   onActionClick={handlePayNow}
 * />
 *
 * <CashbackBanner
 *   type="advance_deposit"
 *   percentage={0.05}
 *   onActionClick={handleDeposit}
 * />
 * ```
 */
export const CashbackBanner: React.FC<CashbackBannerProps> = ({
  type,
  percentage,
  savingsAmount,
  daysLeft,
  onActionClick,
  className,
}) => {
  const { t } = useTranslation('m-payment');

  const isEarlyPay = type === 'early_pay';
  const displayPercentage = Math.round(percentage * 100);

  const title = isEarlyPay ? t('cashback.early_pay_title') : t('cashback.advance_deposit_title');

  const description = isEarlyPay
    ? t('cashback.early_pay_description', { percentage: displayPercentage })
    : t('cashback.advance_deposit_description', { percentage: displayPercentage });

  const badgeLabel = isEarlyPay
    ? savingsAmount
      ? t('cashback.cashback_amount', { amount: savingsAmount })
      : t('cashback.cashback_percentage', { percentage: displayPercentage })
    : t('cashback.advance_deposit_bonus', { percentage: displayPercentage });

  const actionLabel = isEarlyPay ? t('actions.pay_now') : t('actions.add_funds');

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border',
        isEarlyPay
          ? 'bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200'
          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
        className,
      )}>
      {/* Decorative circle */}
      <div
        className={cn(
          'absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10',
          isEarlyPay ? 'bg-teal-500' : 'bg-blue-500',
        )}
      />

      <div className="relative flex items-center justify-between gap-4 p-5">
        {/* Left: Icon + Text */}
        <div className="flex items-start gap-4 flex-1 min-w-0">
          {/* Icon */}
          <div
            className={cn(
              'flex-shrink-0 p-2.5 rounded-xl',
              isEarlyPay ? 'bg-teal-100' : 'bg-blue-100',
            )}>
            <TrendingUpIcon
              className={cn('size-5', isEarlyPay ? 'text-teal-600' : 'text-blue-600')}
            />
          </div>

          {/* Text */}
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
              <CashbackBadge
                percentage={percentage}
                label={badgeLabel}
                daysLeft={isEarlyPay ? daysLeft : undefined}
                variant={'green'}
                isLive
              />
            </div>
            <p className="text-xs text-gray-500 truncate">{description}</p>
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex-shrink-0">
          <PayButton label={actionLabel} size="md" onClick={onActionClick} />
        </div>
      </div>
    </div>
  );
};
